export interface NormalizedSessionMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  isStreaming: false;
  hasBeenParsed: true;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseRecord(value: unknown): UnknownRecord | null {
  if (isRecord(value)) return value;
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeRole(value: unknown): 'user' | 'assistant' {
  return value === 'user' ? 'user' : 'assistant';
}

function extractContentText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => extractContentText(item))
      .filter(Boolean)
      .join('\n');
  }

  if (!isRecord(value)) {
    return '';
  }

  if (typeof value.text === 'string') {
    return value.text;
  }

  if (isRecord(value.text) && typeof value.text.value === 'string') {
    return value.text.value;
  }

  if ('content' in value) {
    return extractContentText(value.content);
  }

  if ('message' in value) {
    return extractContentText(value.message);
  }

  return '';
}

function normalizeTimestamp(value: unknown): Date {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date;
  }

  return new Date();
}

function stringFrom(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

export function normalizeSessionMessage(rawMessage: unknown, index: number): NormalizedSessionMessage | null {
  const raw = parseRecord(rawMessage);
  if (!raw) return null;

  const envelope = parseRecord(raw.message) ?? raw;
  const createdAt = raw.created_at ?? raw.createdAt ?? raw.timestamp ?? envelope.created_at ?? envelope.createdAt ?? envelope.timestamp;
  const timestamp = normalizeTimestamp(createdAt);
  const fallbackId = `${timestamp.toISOString()}-${index}`;

  return {
    id: stringFrom(raw.id) ?? stringFrom(raw.message_id) ?? stringFrom(envelope.id) ?? fallbackId,
    content: extractContentText(envelope.content ?? raw.content ?? raw.message),
    role: normalizeRole(envelope.role ?? raw.role),
    timestamp,
    isStreaming: false,
    hasBeenParsed: true,
  };
}

export function normalizeSessionMessages(payload: unknown): NormalizedSessionMessage[] {
  const source = Array.isArray(payload) ? payload : isRecord(payload) && Array.isArray(payload.messages) ? payload.messages : [];
  return source
    .map((message, index) => normalizeSessionMessage(message, index))
    .filter((message): message is NormalizedSessionMessage => message !== null);
}
